import React, {
  useState, useEffect, useRef, ReactElement, RefObject,
} from 'react';
import { Swiper, SwiperSlide, SwiperRef } from 'swiper/react';
import { Navigation as SwiperNavigation } from 'swiper/modules';
import FormatService from '../../services/format-service';
import ScreenService from '../../services/screen-service';
import { TIMEOUT_MINIMAL, TIMEOUT_SHORT } from '../../constants/system/timeouts';
import 'swiper/css';
import 'swiper/css/navigation';
import './swipeable-slider.styles.scss';

const SwipeableSlider: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const SLIDES_PER_VIEW_AUTO: SwiperSlidesPerViewValue = 'auto';
  const SLIDES_OFFSET: number = 30;
  const SLIDER_CLASSNAME_DARK: string = 'swiper-dark';
  const SLIDER_CLASSNAME_LIGHT: string = 'swiper-light';
  const SLIDER_CLASSNAME_CENTERED: string = 'swiper-centered';
  const SLIDER_CLASSNAME_TRANSPARENT: string = 'swiper-transparent';
  const SLIDER_CLASSNAME_STRETCHED: string = 'swiper-stretched';
  const SLIDER_CLASSNAME_BUTTON_PREV: string = 'swiper-button-prev';
  const SLIDER_CLASSNAME_BUTTON_NEXT: string = 'swiper-button-next';

  const {
    isCentered,
    isDraggable,
    isStretched,
    withNoOffset,
    withControlsVisible,
    withDarkControls,
    withLightControls,
    withTransparentControls,
    slideActiveIndex,
    slideItemWidth,
    slidesPerView,
    children,
  } = props;

  const [isEnabled, setIsEnabled]: [boolean, Function] = useState(false);

  const containerRef: RefObject<SwiperRef> | null = useRef(null);

  const swiperModules: SwiperModule[] | undefined = withControlsVisible ? [SwiperNavigation] : undefined;
  const centeredClassName: string | undefined = isCentered ? SLIDER_CLASSNAME_CENTERED : undefined;
  const darkClassName: string | undefined = withDarkControls ? SLIDER_CLASSNAME_DARK : undefined;
  const lightClassName: string | undefined = withLightControls ? SLIDER_CLASSNAME_LIGHT : undefined;
  const transparentClassName: string | undefined = withTransparentControls ? SLIDER_CLASSNAME_TRANSPARENT : undefined;
  const stretchedClassName: string | undefined = isStretched ? SLIDER_CLASSNAME_STRETCHED : undefined;

  useEffect((): void => {
    if (!isEnabled) {
      setIsEnabled(true);

      setTimeout((): void => {
        if (containerRef && containerRef.current && containerRef.current.swiper) {
          containerRef.current.swiper.enable();
        }
      }, TIMEOUT_SHORT);
    }
  }, [isEnabled]);

  useEffect((): void => {
    if (isEnabled) {
      setTimeout((): void => {
        if (containerRef && containerRef.current && containerRef.current.swiper) {
          const prevButton: HTMLDivElement | null = ScreenService.getElementByClassName(
            SLIDER_CLASSNAME_BUTTON_PREV,
          ) as HTMLDivElement;
          const nextButton: HTMLDivElement | null = ScreenService.getElementByClassName(
            SLIDER_CLASSNAME_BUTTON_NEXT,
          ) as HTMLDivElement;

          if (prevButton) {
            prevButton.addEventListener('click', (): void => {
              if (containerRef.current) {
                containerRef.current.swiper.slidePrev();
              }
            });
          }

          if (nextButton) {
            nextButton.addEventListener('click', (): void => {
              if (containerRef.current) {
                containerRef.current.swiper.slideNext();
              }
            });
          }
        }
      }, TIMEOUT_MINIMAL);
    }
  }, [withControlsVisible]);

  return (
    <Swiper
      enabled={isEnabled}
      ref={containerRef}
      navigation={withControlsVisible}
      modules={swiperModules}
      initialSlide={slideActiveIndex}
      slidesPerView={slidesPerView || SLIDES_PER_VIEW_AUTO}
      slidesOffsetAfter={!withNoOffset && withControlsVisible ? SLIDES_OFFSET : undefined}
      slidesOffsetBefore={!withNoOffset && withControlsVisible ? SLIDES_OFFSET : undefined}
      allowTouchMove={!isDraggable}
      className={FormatService.formatClassNames(
        centeredClassName, darkClassName, lightClassName, transparentClassName, stretchedClassName,
      )}
    >
      {
        children.map((childElement: ReactElement, childElementIndex: number): ReactElement => (
          <SwiperSlide
            key={childElementIndex + 1}
            style={{
              minWidth: slideItemWidth || undefined,
              maxWidth: slideItemWidth || undefined,
            }}
          >
            {childElement}
          </SwiperSlide>
        ))
      }
    </Swiper>
  );
});

export default SwipeableSlider;
